import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { PublicEvent } from '../../shared/api/types';
import { Alert, Button, Card, ErrorState, Field, Loading, Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatWhen } from './shared';
import { CalendarShareButtons } from './AddToCalendar';

/** Invitación pública a un evento: cualquiera la ve y confirma asistencia sin cuenta. */
export function PublicEventPage() {
  const { token = '' } = useParams();
  const data = useAsync((signal) => api.get<PublicEvent>(`/calendar/public/events/${encodeURIComponent(token)}`, signal), [token]);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/calendar/public/events/${encodeURIComponent(token)}/rsvp`, {
        name: String(form.get('name') ?? '').trim(),
        contact: String(form.get('contact') ?? '').trim() || null,
        guests: Number(form.get('guests') || 1),
      });
      setSent(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="landing">
      <header className="landing__bar">
        <Link to="/" aria-label="KEORA">
          <Logo />
        </Link>
      </header>
      <main className="public public-event">
        {data.status === 'loading' ? <Loading /> : null}
        {data.status === 'error' ? <ErrorState error={data.error} /> : null}
        {data.status === 'ready' ? (
          <>
            <p className="eyebrow">{data.data.churchName} te invita</p>
            <h1>{data.data.title}</h1>
            <p className="public-event__when">{formatWhen(data.data.startsAt, data.data.endsAt)}</p>
            <CalendarShareButtons
              event={{
                uid: `e-${token}`,
                title: data.data.title,
                startsAt: data.data.startsAt,
                endsAt: data.data.endsAt,
                location: [data.data.placeName, data.data.address].filter(Boolean).join(', ') || null,
              }}
            />
            {data.data.placeName || data.data.address ? (
              <p>
                {data.data.placeName ? <strong>{data.data.placeName}</strong> : null}
                {data.data.address ? <span className="block muted">{data.data.address}</span> : null}
                {data.data.mapsUrl ? (
                  <a href={data.data.mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                    Cómo llegar
                  </a>
                ) : null}
              </p>
            ) : null}
            {data.data.purpose ? <p className="pre-line">{data.data.purpose}</p> : null}
            <Card title="¿Vienes?">
              {sent ? (
                <Alert tone="success">¡Gracias! Te esperamos.</Alert>
              ) : (
                <form className="form" onSubmit={onSubmit} noValidate>
                  {error ? <Alert>{error}</Alert> : null}
                  <Field label="Tu nombre" name="name" required minLength={2} maxLength={120} autoComplete="name" />
                  <Field label="Teléfono o correo (opcional)" name="contact" maxLength={160} />
                  <Field label="¿Cuántas personas vienen contigo? (contándote)" name="guests" type="number" min={1} max={20} defaultValue={1} />
                  <Button type="submit" busy={busy}>
                    Confirmar asistencia
                  </Button>
                </form>
              )}
            </Card>
          </>
        ) : null}
      </main>
    </div>
  );
}
